import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  completePasswordChange,
  login,
  type CurrentUser,
  type LoginStepUpRequired,
  type PasswordChangeRequired,
} from '../../../shared/features/session/session.api';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { useToast } from '../../../shared/hooks/use-toast';
import { useLoginCodeStep } from './use-login-code-step';

export type LoginStep = 'password' | 'code' | 'newPassword';

interface PasswordFormValues {
  email: string;
  password: string;
}

/** The next step for whatever a correct password (or a completed forced change) just produced — shared by submitPassword and submitNewPassword. */
function advanceFromOutcome(
  result: CurrentUser | LoginStepUpRequired | PasswordChangeRequired,
  callbacks: { toNewPassword: () => void; toCode: (stepUp: LoginStepUpRequired) => void; onSignedIn: (user: CurrentUser) => void },
) {
  if ('passwordChangeRequired' in result) return callbacks.toNewPassword();
  if ('requiresTwoFactor' in result) return callbacks.toCode(result);
  callbacks.onSignedIn(result);
}

/** A second step only when the account has a second factor on or owes a forced password change — otherwise password alone signs in, exactly as before. */
export function useLoginPage() {
  const navigate = useNavigate();
  const { setUser } = useCurrentUser();
  const toast = useToast();
  const [step, setStep] = useState<LoginStep>('password');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function onSignedIn(user: CurrentUser) {
    setUser(user);
    navigate('/', { replace: true });
    toast.success(`Welcome back, ${user.name.split(' ')[0]}`);
  }

  const codeStep = useLoginCodeStep(onSignedIn);

  function toCode(stepUp: LoginStepUpRequired) {
    codeStep.begin(stepUp.methods);
    setStep('code');
  }

  const advanceCallbacks = { toNewPassword: () => setStep('newPassword'), toCode, onSignedIn };

  async function submitPassword(values: PasswordFormValues) {
    setError(null);
    setSubmitting(true);
    try {
      advanceFromOutcome(await login(values), advanceCallbacks);
    } catch {
      // Mirrors the backend's non-disclosure: one generic message for wrong password, unknown email, or a locked-out account.
      setError('Incorrect email or password.');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitNewPassword(password: string) {
    setError(null);
    setSubmitting(true);
    try {
      advanceFromOutcome(await completePasswordChange(password), advanceCallbacks);
    } catch {
      setError("Couldn't set that password. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return { step, error, submitting, submitPassword, submitNewPassword, codeStep };
}
